
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { isAdmin as checkIsAdmin } from "@/services/authService";
import AdminLayout from "@/components/admin/AdminLayout";
import ImageRepositoryHeader from "@/components/admin/ImageRepositoryHeader";
import ImageImportForm from "@/components/admin/ImageImportForm";
import ImageGallery from "@/components/admin/ImageGallery";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2 } from "lucide-react";

const ImageRepository = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  
  // Check if the user is logged in and is admin
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Skip auth check during SSR
        if (typeof window === 'undefined') {
          return;
        }
        
        const isAdmin = await checkIsAdmin();
        if (!isAdmin) {
          throw new Error("Not authorized as admin");
        }
        
        // Success - user is authenticated and is an admin
        setIsLoading(false);
        setAuthError(null);
      } catch (error: any) {
        console.error("Authentication error:", error);
        setAuthError(error.message || "Authentication failed");
        
        toast({
          title: "Access denied",
          description: "Please login to access the image repository",
          variant: "destructive",
        });
        
        // Add a slight delay before navigation to ensure the component doesn't unmount too quickly
        setTimeout(() => {
          navigate("/admin/login");
        }, 100);
      }
    };
    
    // Skip auth check during SSR
    if (typeof window !== 'undefined') {
      checkAuth();
    } else {
      // For SSR, just render the loading state
      setIsLoading(true);
    }
  }, [navigate, toast]);

  // For SSR initial render - show minimal loading state
  if (typeof window === 'undefined') {
    return <div>Loading image repository...</div>;
  }

  // Error state
  if (authError && !isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <h2 className="text-xl font-bold text-red-600 mb-4">Authentication Error</h2>
          <p className="text-gray-700 mb-4">{authError}</p>
          <button 
            onClick={() => navigate("/admin/login")}
            className="w-full bg-elfGreen text-white py-2 px-4 rounded hover:bg-elfGreen-dark transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-elfGreen mx-auto mb-4" />
          <p className="text-lg">Loading image repository...</p>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout 
      title="Image Repository"
      backLink="/admin/dashboard"
      backLabel="Back to Dashboard"
    >
      <ImageRepositoryHeader />
      
      <Tabs defaultValue="import" className="mt-6">
        <TabsList>
          <TabsTrigger value="import">Import Images</TabsTrigger>
          <TabsTrigger value="gallery">Image Gallery</TabsTrigger>
        </TabsList>
        
        <TabsContent value="import" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Import Images</CardTitle>
            </CardHeader>
            <CardContent>
              <ImageImportForm />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="gallery" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Image Gallery</CardTitle>
            </CardHeader>
            <CardContent>
              <ImageGallery />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default ImageRepository;
